import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDays, endOfDay, format, isAfter, parseISO, startOfDay } from 'date-fns';
import { useBalance, useDistribution } from '../../hooks';
import { formatThousands } from '../../utils';
import { clearByModuleFromLocalStorage } from '../../store';
import { Balance } from '../../dtos';
import { ImageHandler } from '../../components';
import { MainLayout } from '../../components/layouts';
import { VoucherIcon } from '../../components/icons';
import {
    FilterButton,
    Container,
    Header,
    ExhibitorHeader,
    AmountHeader,
    Content,
    NoData
} from './styles';

const filters = [5, 10, 15, 30, 60];

const BalancePage: React.FC = () => {
    const [filteredBalances, setFilteredBalances] = useState<Balance[]>([]);
    const [selectedFilter, setSelectedFilter] = useState<number | null>(null);

    const navigate = useNavigate();
    const { balances } = useBalance();
    const { clearDistributionState, selectVoucherType } = useDistribution();

    useEffect(() => {
        if (selectedFilter === null) {
            setFilteredBalances(balances);
        } else {
            setFilteredBalances(balances.filter(balance => {
                const dueDate = startOfDay(parseISO(balance.dueDate));
                const maxDate = addDays(endOfDay(new Date()), selectedFilter - 1);

                return !isAfter(dueDate, maxDate);
            }));
        }
    }, [selectedFilter, balances]);

    return (
        <MainLayout id="balance" title="Saldo de cortesias" hiddenMarginContent>
            {balances.length > 0 ? (
                <Container>
                    <div>
                        <span>Vencimento em até:</span>
                        <div>
                            {filters.map(filter => (
                                <FilterButton
                                    id={`balance.filters.${filter}daysbutton`}
                                    key={filter}
                                    outlined={filter !== selectedFilter}
                                    onClick={() => setSelectedFilter(oldSelectedFilter => oldSelectedFilter === filter ? null : filter)}
                                >
                                    {`${filter} dias`}
                                </FilterButton>
                            ))}
                        </div>
                    </div>
                    <ul>
                        {filteredBalances.map((balance, index) => (
                            <li key={index}>
                                <span>{format(parseISO(balance.dueDate), 'dd/MM/yyyy')}</span>
                                {balance.exhibitors.map((exhibitor, exhibitorIdx) => (
                                    <Fragment key={exhibitorIdx}>
                                        {exhibitor.voucherTypes.map((voucherType, voucherTypeIdx) => (
                                            <Content
                                                id={`balance.exhibitors${exhibitorIdx}.voucherTypes${voucherTypeIdx}.box`}
                                                key={voucherTypeIdx}
                                                onClick={() => {
                                                    clearDistributionState(false);
                                                    clearByModuleFromLocalStorage('distribution');
                                                    selectVoucherType(exhibitor, voucherType, balance.dueDate);
                                                    navigate('/distribution', {
                                                        replace: true,
                                                        state: {
                                                            isBalance: true
                                                        }
                                                    });
                                                }}
                                            >
                                                <Header>
                                                    <ExhibitorHeader>
                                                        <ImageHandler
                                                            url={exhibitor.logo}
                                                            alt={exhibitor.name}
                                                        />
                                                        <div>
                                                            <strong>{exhibitor.name}</strong>
                                                            <small>{voucherType.name}</small>
                                                        </div>
                                                    </ExhibitorHeader>
                                                    <AmountHeader>
                                                        <span><VoucherIcon /></span>
                                                        <span>{formatThousands(voucherType.amount)}</span>
                                                    </AmountHeader>
                                                </Header>
                                                <span>Usar</span>
                                            </Content>
                                        ))}
                                    </Fragment>
                                ))}
                            </li>
                        ))}
                    </ul>
                </Container>
            ) : (
                <NoData>Nenhum saldo disponível!</NoData>
            )}
        </MainLayout>
    );
};

export { BalancePage };
